import React, { useEffect, useState } from 'react'
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import generateCaptcha from '../../components/captcha';
import formatDateTime from '../../components/formatDateTime';
import { MdDeleteSweep } from "react-icons/md";
import { IoIosSend } from "react-icons/io";
import FileUpload from '../../components/ul/FileUpload';
import { imageSizeExceededMessage, imageType, invalidImageMessage, MAX_FILE_SIZE_IMAGE } from '../../components/imageType';
import img1 from '../../Image/NLogo.svg';
import NotFound from '../../components/ul/NotFound';
import UploadButton from '../../components/ul/UploadButton';
import DeleteButton from '../../components/ul/DeleteButton';

const Notification = () => {
    const [loader, setLoader] = useState(false);
    const [data, setData] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [fileProcessing, setFileProcessing] = useState({
        image: false,
    });
    const [fileStatus, setFileStatus] = useState({
        image: null, // 'success', 'error', or null
    });
    const [file, setFile] = useState(null);
    const [formInfo, setFormInfo] = useState({
        title: '',
        message: '',
        url: '',
    });

    const fetchData = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getNotificationHistory();
            if (resp?.status) {
                setData(resp.data);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error(error)
        } finally {
            setLoader(false);
        }
    }

    useEffect(() => {
        fetchData();
    }, [])

    const handleDeleteSelected = async (id) => {
        const content = `You are about to delete a notification. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (check) {
            try {
                setLoader(true);
                let body = {};
                if (id) {
                    body.id = [id];
                } else {
                    body.id = selectedUsers;
                }

                const response = await Calling.deleteNotification(body);
                if (response?.status) {
                    toast.success(response?.message || 'notification deleted successfully!');
                    setSelectedUsers([]);
                    setSelectAll(false); // Reset "Select All"
                    fetchData();
                } else {
                    toast.error(response?.message);
                }
            } catch (error) {
                console.error('Error deleting users:', error);
            } finally {
                setLoader(false);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    };

    const handleSelectAll = () => {
        if (!selectAll) {
            const allUserIds = data.map((user) => user._id);
            setSelectedUsers(allUserIds);
        } else {
            setSelectedUsers([]);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectUser = (id) => {
        if (selectedUsers.includes(id)) {
            setSelectedUsers(selectedUsers.filter((userId) => userId !== id));
        } else {
            setSelectedUsers([...selectedUsers, id]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoader(true);
        try {
            const formData = new FormData();
            formData.append('title', formInfo?.title || '');
            formData.append('message', formInfo?.message || '');
            formData.append('url', formInfo?.url || '');
            if (file) {
                formData.append('image', file);
            }
            const response = await Calling.sendNotification(formData);
            if (response?.status) {
                toast.success(response?.message || 'Notification sent successfully!');
                setFormInfo({ title: '', message: '', url: '' });
                setFile(null);
                setFileProcessing({
                    image: false,
                });
                setFileStatus({
                    image: null,
                });
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            fetchData();
            setLoader(false);
        }
    }

    const handleChangefile = (e) => {
        const selectedFile = e.target.files[0];

        setFileProcessing({ image: true }); // Start processing

        // Validate the MIME type
        if (selectedFile && !imageType.includes(selectedFile.type)) {
            toast.error(invalidImageMessage);
            setFile(null); // Reset file if invalid
            setFileProcessing({ image: false }); // Stop processing
            setFileStatus({ image: 'error' }); // Set error status
            e.target.value = '';
            return;
        } else {
            if (selectedFile?.size > MAX_FILE_SIZE_IMAGE) {
                setFile(null);
                setFileProcessing({ image: false }); // Stop processing
                setFileStatus({ image: 'error' });
                toast.error(imageSizeExceededMessage);
                e.target.value = '';
                return;
            }
            setFile(selectedFile); // Set the file if valid
            setFileStatus({ image: 'success' }); // Set success status
            setFileProcessing({ image: false }); // Stop processing
        }

    };

    const handleFormChange = (e) => {
        setFormInfo({ ...formInfo, [e.target.name]: e.target.value });
    }

    const handleSend = async (info) => {
        const content = `You are about to Resend a notification. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (check) {
            setLoader(true);

            try {
                let body = {
                    'title': info?.n_title || '',
                    'message': info?.n_message || '',
                    'url': info?.n_url || ''
                }

                if (info?.n_icon) {
                    body.fileName = info?.n_icon || ''
                }
                const response = await Calling.ResendNotification(body);
                if (response?.status) {
                    toast.success(response?.message || 'Notification sent successfully!');
                } else {
                    toast.error(response?.message);
                }
            } catch (error) {
                console.error('Error submitting form:', error);
            } finally {
                fetchData();
                setLoader(false);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    }

    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className='px-3 sm:px-4 lg:ml-[4.4rem] w-full text-main-font mt-16 md:mt-20'>
                    <div className="bg-main-white w-full mt-4 shadow-lg rounded-lg">
                        <div className='p-4 sm:p-6'>
                            <h1 className="text-xl sm:text-2xl font-bold mb-1">Notification Send</h1>
                            <hr className='border-main-border mb-4' />
                            <form onSubmit={handleSubmit} encType="multipart/form-data" className="">
                                {/* Title Field */}
                                <div className="grid grid-cols-12 gap-2 items-center mb-4">
                                    <label className="col-span-12 sm:col-span-2 md:col-span-1 text-main-font font-medium" htmlFor="title">Title:</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formInfo?.title}
                                        onChange={(e) => handleFormChange(e)}
                                        id="title"
                                        className="col-span-12 sm:col-span-10 md:col-span-11 p-2 border border-main-border rounded mt-1 focus:outline-none focus:ring-2 focus:ring-main-subfont"
                                        placeholder="Enter Title"
                                        required
                                    />
                                </div>

                                {/* Message Field */}
                                <div className="grid grid-cols-12 gap-2 items-center mb-4">
                                    <label className="col-span-12 sm:col-span-2 md:col-span-1 text-main-font font-medium" htmlFor="message">Message:</label>
                                    <input
                                        type="text"
                                        name="message"
                                        value={formInfo?.message}
                                        onChange={(e) => handleFormChange(e)}
                                        id="message"
                                        className="col-span-12 sm:col-span-10 md:col-span-11 p-2 border border-main-border rounded mt-1 focus:outline-none focus:ring-2 focus:ring-main-subfont"
                                        placeholder="Enter Message"
                                        required
                                    />
                                </div>

                                {/* URL Field */}
                                <div className="grid grid-cols-12 gap-2 items-center mb-4">
                                    <label className="col-span-12 sm:col-span-2 md:col-span-1 text-main-font font-medium" htmlFor="url">URL:</label>
                                    <input
                                        type="text"
                                        name="url"
                                        value={formInfo?.url}
                                        onChange={(e) => handleFormChange(e)}
                                        id="url"
                                        className="col-span-12 sm:col-span-10 md:col-span-11 p-2 border border-main-border rounded mt-1 focus:outline-none focus:ring-2 focus:ring-main-subfont"
                                        placeholder="Enter URL"
                                    />
                                </div>

                                {/* Icon Upload */}
                                <div className="grid grid-cols-12 gap-2 items-center mb-4">
                                    <label className="col-span-12 sm:col-span-2 md:col-span-1 text-main-font font-medium" htmlFor="icon">Icon:</label>
                                    <div className='relative col-span-12 sm:col-span-10 md:col-span-11'>
                                        {fileProcessing['image'] && <div className='absolute inset-0 flex items-center justify-center'><div className='loader'></div></div>}
                                        {fileStatus['image'] === 'error' && <div className='absolute inset-0 flex mt-3 sm:ml-[-20px] text-xl text-red-500'>✘</div>}
                                        {fileStatus['image'] === 'success' && <div className='absolute inset-0 flex mt-3 sm:ml-[-20px] text-xl text-green-500'>✓</div>}
                                        <FileUpload id='image' name='image' accept='image/*' onChange={handleChangefile} required={false} multiple={false} />
                                        <p className={`text-[12px] mt-1 ${fileStatus['image'] === 'error' ? 'text-main-delete' : 'text-main-subfont'}`}>Only PNG and JPG JPEJ files are allowed.</p>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="flex">
                                    <UploadButton className='w-full sm:w-auto' />
                                </div>
                            </form>

                            {/* Delete Selected Button */}
                            <div className="mt-5">
                                {selectedUsers.length > 0 && (
                                    <div className="mb-4">
                                        <button
                                            onClick={() => handleDeleteSelected(null)}
                                            className="bg-red-500 text-main-white px-4 py-2 rounded hover:bg-red-700"
                                        >
                                            Delete Selected
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <hr className='border-main-border border-[2px]' />

                        {/* Responsive Table */}
                        <div className="overflow-x-auto">
                            <table className='w-full mt-4 rounded-lg overflow-hidden min-w-[1000px]'>
                                <thead className='bg-main-th text-main-font'>
                                    <tr>
                                        <th className="ps-6 py-3 flex gap-3 mt-1 text-left text-sm font-medium uppercase">
                                            <input
                                                type="checkbox"
                                                checked={selectAll}
                                                onChange={handleSelectAll}
                                            />
                                        </th>
                                        <th className="px-3 py-3 text-left text-sm font-medium uppercase">#</th>
                                        <th className="px-3 py-3 text-left text-sm font-medium uppercase">Icon</th>
                                        <th className="px-3 py-3 text-left text-sm font-medium uppercase">Title</th>
                                        <th className="px-3 py-3 text-left text-sm font-medium uppercase">Message</th>
                                        <th className="px-3 py-3 text-left text-sm font-medium uppercase">Url</th>
                                        <th className="px-3 py-3 text-left text-sm font-medium uppercase">Date</th>
                                        <th className="px-3 py-3 text-left text-sm font-medium uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.length > 0 ?
                                        data.map((record, index) => (
                                            <tr key={record._id} className="hover:bg-gray-50">
                                                <td className="ps-6 border text-left">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedUsers.includes(record._id)}
                                                        onChange={() => handleSelectUser(record._id)}
                                                    />
                                                </td>
                                                <td className="p-2 border">{index + 1}</td>
                                                <td className="p-2 border">
                                                    <img
                                                        src={record.n_icon || img1}
                                                        alt="icon"
                                                        className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover"
                                                    />
                                                </td>
                                                <td className="p-2 border max-w-[150px] sm:max-w-[200px] truncate">
                                                    {record.n_title}
                                                </td>
                                                <td className="p-2 border max-w-[150px] sm:max-w-[200px] truncate">
                                                    {record.n_message}
                                                </td>
                                                <td className="p-2 border">
                                                    {record.n_url && (
                                                        <a
                                                            href={record.n_url}
                                                            target="_blank"
                                                            rel="noreferrer noopener"
                                                            className="bg-green-600 text-main-white px-2 py-1 sm:px-3 sm:py-2 rounded hover:bg-green-500 text-sm sm:text-base"
                                                        >
                                                            Url
                                                        </a>
                                                    )}
                                                </td>
                                                <td className="p-2 border">{formatDateTime(record.createdAt)}</td>
                                                <td className="p-2 border py-3 sm:py-5 flex items-center gap-2 sm:gap-4 justify-start">
                                                    <button
                                                        onClick={() => handleSend(record)}
                                                        className='bg-green-500 hover:bg-green-400 px-2 py-2 sm:px-4 sm:py-3 rounded-lg text-white'
                                                    >
                                                        <IoIosSend size={16} />
                                                    </button>
                                                    <DeleteButton onClick={() => handleDeleteSelected(record._id)} />
                                                </td>
                                            </tr>
                                        ))
                                        : (
                                            <tr>
                                                <td
                                                    colSpan="8"
                                                    className="text-center border px-4 py-2"
                                                >
                                                    <NotFound />
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View for Data (shows on small screens only) */}
                        <div className="sm:hidden px-4 pb-6">
                            {data.length > 0 ? (
                                data.map((record, index) => (
                                    <div key={record._id} className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200">
                                        <div className="flex justify-between items-center mb-3">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.includes(record._id)}
                                                    onChange={() => handleSelectUser(record._id)}
                                                />
                                                <span className="font-medium">#{index + 1}</span>
                                            </div>
                                            <img
                                                src={record.n_icon || img1}
                                                alt="icon"
                                                className="w-12 h-12 rounded-full object-cover"
                                            />
                                        </div>

                                        <div className="mb-2">
                                            <span className="font-medium text-sm">Title: </span>
                                            <span>{record.n_title}</span>
                                        </div>

                                        <div className="mb-2">
                                            <span className="font-medium text-sm">Message: </span>
                                            <span>{record.n_message}</span>
                                        </div>

                                        <div className="mb-2">
                                            <span className="font-medium text-sm">Date: </span>
                                            <span>{formatDateTime(record.createdAt)}</span>
                                        </div>

                                        <div className="flex items-center justify-between mt-3">
                                            {record.n_url && (
                                                <a
                                                    href={record.n_url}
                                                    target="_blank"
                                                    rel="noreferrer noopener"
                                                    className="bg-green-600 text-main-white px-2 py-1 rounded hover:bg-green-500 text-sm"
                                                >
                                                    Url
                                                </a>
                                            )}

                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleSend(record)}
                                                    className='bg-green-500 hover:bg-green-400 px-2 py-2 rounded-lg text-white'
                                                >
                                                    <IoIosSend size={16} />
                                                </button>
                                                <DeleteButton onClick={() => handleDeleteSelected(record._id)} />
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="py-4">
                                    <NotFound />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default Notification